import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { useForm } from 'react-hook-form';
import {
    UtilisateurChoice,
    UtilisateurSearchList,
    useModal,
} from '@europrocurement/flexy-components';
import { useUtilisateur } from '@europrocurement/l2d-modules/hooks/useUtilisateur';
import { User, useUsersCtx } from '@europrocurement/l2d-modules/providers';

export type FormalisteAssignModalProps = {
    /** Formaliste actuellement sélectionné */
    selectedFormaliste?: User;
    /** Fonction appelée lors de l'assignation d'un formaliste */
    onFormalisteAssign: (formaliste: User, message?: string) => void;
    /** État de chargement pendant l'assignation */
    isLoading?: boolean;
    /** Titre personnalisé pour la modal */
    title?: string;
    /** Texte du bouton de soumission */
    submitButtonText?: string;
};

/**
 * Modal d'assignation d'un formaliste à un dossier.
 * Conçue pour être utilisée avec useModal() et modalActions.call()
 */
const FormalisteAssignModal: React.FunctionComponent<FormalisteAssignModalProps> = function ({
    selectedFormaliste: initialSelectedFormaliste,
    onFormalisteAssign,
    isLoading = false,
    title = 'Assigner un formaliste',
    submitButtonText = 'Assigner',
}) {
    const { palette } = useTheme();
    const { modalActions } = useModal();
    const { searchFormalistes } = useUtilisateur();
    const { getUserById } = useUsersCtx();

    const userToUtilisateurChoice = (u: User) => ({
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.mail,
    });

    // État local pour le formaliste sélectionné
    const [selectedFormaliste, setSelectedFormaliste] = useState<UtilisateurChoice | undefined>(
        initialSelectedFormaliste ? userToUtilisateurChoice(initialSelectedFormaliste) : undefined,
    );

    // État pour les résultats de recherche
    const [displayedFormalistes, setDisplayedFormalistes] = useState<UtilisateurChoice[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Synchronisation avec la prop externe
    useEffect(() => {
        setSelectedFormaliste(
            initialSelectedFormaliste
                ? userToUtilisateurChoice(initialSelectedFormaliste)
                : undefined,
        );
    }, [initialSelectedFormaliste]);

    // Mettre à jour les formalistes affichés quand la liste initiale change
    useEffect(() => {
        setDisplayedFormalistes(
            searchFormalistes()
                .map(userToUtilisateurChoice)
                .sort((a, b) => a.lastName.localeCompare(b.lastName))
                .slice(0, 20),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fonction de recherche avec debounce
    const handleSearch = async (searchTerm?: string) => {
        setIsSearching(true);
        try {
            const results = searchFormalistes(searchTerm);
            setDisplayedFormalistes(
                results
                    .map(userToUtilisateurChoice)
                    .sort((a, b) => a.lastName.localeCompare(b.lastName))
                    .slice(0, 20),
            );
        } catch (error) {
            console.error('Erreur lors de la recherche de formalistes:', error);
        } finally {
            setIsSearching(false);
        }
    };

    /**
     * Configuration du formulaire pour le message optionnel
     */
    const formStructure: FormStructure[] = [
        {
            type: 'textarea',
            name: 'message',
            inputlabel: 'Message (optionnel)',
            inputProps: {
                multiline: true,
                maxRows: Infinity,
                placeholder: 'Ajouter une note concernant cette assignation...',
            },
            sx: {
                minHeight: '100px',
                padding: '8px 20px',
                border: `1px solid ${palette.grey['300']}`,
                borderRadius: '12px',
                ':hover': {
                    border: `1px solid ${palette.grey['900']}`,
                },
                ':focus-within': {
                    border: `1px solid ${palette.primary.main}`,
                },
                transition: 'all ease-in-out 300ms',
                '&::before': { display: 'none' },
                '&::after': { display: 'none' },
            },
        },
    ];

    const formContext = useForm({
        mode: 'onTouched',
    });

    const formProps = {
        formContext,
        formObject: {},
        onSubmit: () => {},
        submitButton: {
            displayed: false,
        },
    };

    /**
     * Gère l'assignation du formaliste avec message optionnel
     */
    const handleAssignFormaliste = () => {
        if (!selectedFormaliste || isLoading) {
            return;
        }

        const { message } = formContext.getValues();
        const trimmedMessage = message?.trim();
        const newFormaliste = getUserById(selectedFormaliste.id);

        if (!newFormaliste) {
            return;
        }

        onFormalisteAssign(
            newFormaliste,
            trimmedMessage && trimmedMessage.length > 0 ? trimmedMessage : undefined,
        );

        // Fermer la modal après assignation
        modalActions.close();
    };

    /**
     * Détermine si le bouton d'assignation doit être actif
     */
    const isAssignButtonEnabled = selectedFormaliste && !isLoading;

    return (
        <Box
            sx={{
                width: 600,
                padding: '30px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
            }}
        >
            {/* Titre */}
            <Typography variant="l2dh6">{title}</Typography>

            {/* Corps de la modal */}
            <Box sx={{ width: '100%', gap: '8px', display: 'grid' }}>
                {/* Liste des formalistes */}
                <UtilisateurSearchList
                    users={displayedFormalistes}
                    selectedUser={selectedFormaliste}
                    onSelectUser={setSelectedFormaliste}
                    listTitle="Formalistes disponibles"
                    onSearch={handleSearch}
                    isSearching={isSearching}
                />

                {/* Zone de message */}
                <Box>
                    <FlexyForm
                        formStructure={formStructure}
                        {...formProps}
                    />
                </Box>
            </Box>

            {/* Actions */}
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'end',
                }}
            >
                <Box sx={{ display: 'flex', gap: '16px' }}>
                    {/* Bouton Annuler */}
                    <Button
                        onClick={() => modalActions.close()}
                        variant="outlined"
                        disabled={isLoading}
                        sx={{
                            padding: '16px 24px',
                            fontSize: '16px',
                        }}
                    >
                        Annuler
                    </Button>

                    {/* Bouton d'assignation avec état de chargement */}
                    {isLoading ? (
                        <Button
                            variant="contained"
                            disabled
                            sx={{
                                padding: '16px 24px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minWidth: '6rem',
                                fontSize: '16px',
                            }}
                        >
                            <CircularProgress
                                color="info"
                                sx={{
                                    maxHeight: '15px',
                                    maxWidth: '15px',
                                    color: 'common.white',
                                }}
                            />
                        </Button>
                    ) : (
                        <Button
                            onClick={handleAssignFormaliste}
                            variant="contained"
                            disabled={!isAssignButtonEnabled}
                            sx={{
                                padding: '16px 24px',
                                fontSize: '16px',
                                transition: 'all ease-in-out 300ms',
                                backgroundColor: isAssignButtonEnabled
                                    ? palette.primary.main
                                    : palette.grey[300],
                                ':hover': {
                                    backgroundColor: isAssignButtonEnabled
                                        ? palette.primary.dark
                                        : palette.grey[300],
                                },
                            }}
                        >
                            {submitButtonText}
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default FormalisteAssignModal;
